import { z } from 'zod';
import { ORIGEM_ALUNO, STATUS_ALUNO, } from '../dominio/index.js';
import { dataValida } from '../tempo/index.js';
import { zDataISO, zId, zPaginacao, zTelefone } from './primitivos.js';
/**
 * Os contratos de alunos — `#v-alunos`, `#m-aluno` e `#m-novo-aluno`.
 *
 * "Base única de pessoas: mensalista, avulso e experimental." Uma tabela
 * para todo mundo; experimental, mensalista e avulso são ESTADOS, não
 * entidades. O cadastro é POR ARENA — decisão do dono: a mesma pessoa em
 * duas unidades é dois cadastros.
 *
 * Três regras daqui valem nos dois lados e por isso moram neste arquivo:
 *
 *   a normalização da busca    o termo digitado precisa virar exatamente
 *                              o que a coluna gerada `nome_busca` guarda
 *   a regra do menor de idade  depende de "hoje" — no FUSO DA ARENA,
 *                              que o chamador informa
 *   o ciclo de vida            quais transições cada situação admite
 *
 * Ver: Arena_backend/Docs/plano/08-alunos.md
 */
// ---------------------------------------------------------------------
// Vocabulário
// ---------------------------------------------------------------------
export const ROTULO_STATUS_ALUNO = {
    Experimental: 'Experimental',
    Ativo: 'Ativo',
    Inativo: 'Inativo',
    Cancelado: 'Cancelado',
};
/** "Como conheceu a arena" — os rótulos do select de `#m-aluno`. */
export const ROTULO_ORIGEM_ALUNO = {
    Indicacao: 'Indicação de aluno',
    Instagram: 'Instagram',
    Site: 'Site',
    PassouEmFrente: 'Passou em frente',
    Importacao: 'Importação de planilha',
};
/**
 * O que se escolhe ao cadastrar. `#m-novo-aluno` oferece só estes dois:
 * ninguém nasce inativo ou cancelado — isso é transição, com histórico.
 */
export const STATUS_INICIAIS = ['Experimental', 'Ativo'];
/**
 * Os dois consentimentos de `#m-novo-aluno` e da aba LGPD.
 *
 * SEPARADOS de propósito: consentimento agrupado não é consentimento
 * válido. O termo é obrigatório no cadastro; o uso de imagem é opcional
 * e revogável a qualquer momento — e revogá-lo não toca o termo.
 */
export const TIPOS_DE_CONSENTIMENTO = ['TermoDeUso', 'UsoDeImagem'];
export const ROTULO_CONSENTIMENTO = {
    TermoDeUso: 'Termo de uso e política de privacidade',
    UsoDeImagem: 'Uso de imagem em redes sociais',
};
/** Só o de imagem se revoga por aqui. O termo encerra com o cadastro. */
export const CONSENTIMENTOS_REVOGAVEIS = ['UsoDeImagem'];
export const MAIORIDADE_ANOS = 18;
// ---------------------------------------------------------------------
// Ciclo de vida — Experimental → Ativo → Inativo → Cancelado
//
// Status muda por CASO DE USO, não por edição de campo: "cancelar" exige
// motivo e registra histórico, e um campo livre no formulário perderia as
// duas coisas. A tabela abaixo é a regra inteira; o servidor a aplica e o
// front a usa para mostrar só os botões que fazem sentido.
// ---------------------------------------------------------------------
export const CASOS_DE_STATUS = ['ativar', 'inativar', 'cancelar'];
export const TRANSICOES = {
    // Reativar um cancelado é permitido enquanto ele não foi anonimizado:
    // a pessoa voltou, e o histórico dela continua o mesmo.
    ativar: {
        de: ['Experimental', 'Inativo', 'Cancelado'],
        para: 'Ativo',
        exigeMotivo: false,
        rotulo: 'Ativar',
    },
    inativar: {
        de: ['Experimental', 'Ativo'],
        para: 'Inativo',
        exigeMotivo: false,
        rotulo: 'Inativar',
    },
    cancelar: {
        de: ['Experimental', 'Ativo', 'Inativo'],
        para: 'Cancelado',
        exigeMotivo: true,
        rotulo: 'Cancelar cadastro',
    },
};
/** As transições que um cadastro nesta situação admite. Anonimizado não admite nenhuma. */
export function casosPossiveis(status, anonimizado) {
    if (anonimizado)
        return [];
    return CASOS_DE_STATUS.filter((caso) => TRANSICOES[caso].de.includes(status));
}
// ---------------------------------------------------------------------
// Busca sem acento
// ---------------------------------------------------------------------
/**
 * Letras que o NFD não decompõe e o `unaccent` do Postgres troca. A lista
 * vem do `unaccent.rules` e existe por PARIDADE: o termo normalizado aqui
 * é comparado com a coluna gerada lá, e uma divergência vira "digitei
 * certo e não achou". Um teste do backend confere as duas pontas.
 */
const SEM_DECOMPOSICAO = {
    æ: 'ae',
    œ: 'oe',
    ø: 'o',
    ß: 'ss',
    ł: 'l',
    đ: 'd',
    ð: 'd',
    þ: 'th',
    ı: 'i',
    ħ: 'h',
    ŧ: 't',
    ŋ: 'n',
    ĸ: 'q',
};
/**
 * "Jéssica Prado" → "jessica prado". O mesmo que `private.sem_acento()`
 * faz no banco, e na mesma ordem: tira o acento, depois põe em
 * minúsculas.
 *
 * O protótipo já normalizava assim (`normalize('NFD')` e o intervalo de
 * diacríticos); aqui ganhou as letras sem decomposição.
 */
export function normalizarParaBusca(texto) {
    return (texto
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        // O que sobra fora do ASCII básico são as letras que o NFD não
        // decompôs. `\P{ASCII}` (o binário Unicode, negado) em vez de
        // um intervalo por escape (`[^\x00-\x7f]`): a forma por
        // intervalo cita \x00 e \x7f dentro do padrão — caracteres de
        // controle —, e o `no-control-regex` do ESLint recusa isso
        // mesmo escapado; o Prettier, por sua vez, ao formatar reescreve
        // `\u0080`/`\uffff` de volta para os bytes brutos, reintroduzindo um
        // caractere de controle literal no arquivo-fonte. A propriedade
        // Unicode evita as duas armadilhas.
        .replace(/\P{ASCII}/gu, (letra) => SEM_DECOMPOSICAO[letra] ?? letra));
}
/**
 * O termo como ele vai para o `LIKE`: normalizado, com espaços
 * colapsados, e sem os curingas do SQL — `%` e `_` digitados são texto,
 * não "qualquer coisa", e nome de pessoa não os tem.
 */
export function termoDeBusca(texto) {
    return normalizarParaBusca(texto)
        .replace(/[%_\\]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
/** Os dígitos do termo — para achar por trecho de telefone ou de CPF. */
export function digitosDaBusca(texto) {
    return texto.replace(/\D/g, '');
}
/** Menos que isto não filtra telefone: "62" casaria a arena inteira. */
export const MINIMO_DE_DIGITOS_NA_BUSCA = 3;
// ---------------------------------------------------------------------
// Idade — no fuso da arena, sempre
// ---------------------------------------------------------------------
/**
 * Anos completos em `hoje`. As duas datas são de calendário: "hoje" é o
 * dia no FUSO DA ARENA, que quem chama calcula com `hojeNoFuso`. Às 22h
 * de 11/09 em Goiânia já é 12/09 em UTC — e a pessoa que faz 18 anos no
 * dia 12 ainda é menor na recepção.
 *
 * Nascido em 29/02 completa ano em 01/03 nos anos que não são bissextos.
 */
export function idadeEm(nascimento, hoje) {
    const anos = Number(hoje.slice(0, 4)) - Number(nascimento.slice(0, 4));
    return hoje.slice(5) < nascimento.slice(5) ? anos - 1 : anos;
}
export function ehMenorDeIdade(nascimento, hoje) {
    return idadeEm(nascimento, hoje) < MAIORIDADE_ANOS;
}
// ---------------------------------------------------------------------
// Primitivos
// ---------------------------------------------------------------------
/** CPF pelo dígito verificador — "000.000.000-00" do protótipo não passa. */
export function cpfValido(digitos) {
    if (!/^\d{11}$/.test(digitos) || /^(\d)\1{10}$/.test(digitos))
        return false;
    const verificador = (base) => {
        let soma = 0;
        for (let i = 0; i < base.length; i += 1)
            soma += Number(base[i]) * (base.length + 1 - i);
        const resto = (soma * 10) % 11;
        return resto === 10 ? 0 : resto;
    };
    return (verificador(digitos.slice(0, 9)) === Number(digitos[9]) &&
        verificador(digitos.slice(0, 10)) === Number(digitos[10]));
}
/** CPF normalizado para dígitos. Opcional no cadastro; quando vem, vem certo. */
export const zCpf = z
    .string()
    .trim()
    .transform((v) => v.replace(/\D/g, ''))
    .refine(cpfValido, { message: 'CPF inválido — confira os dígitos.' });
/** Nome de pessoa: espaços colapsados, ao menos nome e uma letra a mais. */
export const zNomeDePessoa = z
    .string()
    .trim()
    .transform((v) => v.replace(/\s+/g, ' '))
    .pipe(z.string().min(3, 'Informe o nome completo.').max(120, 'No máximo 120 caracteres.'));
const textoOpcional = (maximo) => z.string().trim().max(maximo, `No máximo ${maximo} caracteres.`);
/** Data de calendário que existe — o formato sozinho aceita 30 de fevereiro. */
const zDataReal = zDataISO.refine(dataValida, 'Essa data não existe no calendário.');
// ---------------------------------------------------------------------
// Entrada
// ---------------------------------------------------------------------
const camposDoCadastro = {
    nome: zNomeDePessoa,
    apelido: textoOpcional(40).optional(),
    telefone: zTelefone,
    nascimento: zDataReal,
    cpf: zCpf.optional(),
    email: z.string().trim().toLowerCase().email('E-mail inválido.').optional(),
    origem: z.enum(ORIGEM_ALUNO).optional(),
    contatoEmergenciaNome: textoOpcional(80).optional(),
    contatoEmergenciaTelefone: zTelefone.optional(),
    /** Obrigatório quando o aluno é menor de idade — ver `regrasDoCadastro`. */
    responsavelFinanceiro: textoOpcional(120).optional(),
    /**
     * Dado de SAÚDE. Só é aceito de quem tem a projeção que o enxerga —
     * quem não pode ler a observação não a sobrescreve às cegas.
     */
    observacoesMedicas: textoOpcional(1000).optional(),
};
/**
 * O corpo de `POST /alunos` — `#m-novo-aluno`.
 *
 * Obrigatórios: nome, telefone, nascimento, status e o aceite do termo.
 * O plano e a turma do protótipo chegam com os documentos 12 e 10.
 *
 * `desde` é o início do vínculo. A tela não o pede — é hoje —; o seed e a
 * importação do documento 09 informam o de verdade.
 */
export const zNovoAluno = z.object({
    ...camposDoCadastro,
    status: z.enum(STATUS_INICIAIS, {
        errorMap: () => ({ message: 'No cadastro, o status é Experimental ou Ativo.' }),
    }),
    desde: zDataReal.optional(),
    aceiteTermo: z.literal(true, {
        errorMap: () => ({ message: 'O aceite do termo de uso e da política é obrigatório.' }),
    }),
    consentimentoImagem: z.boolean().default(false),
});
export const MENSAGEM_RESPONSAVEL_DO_MENOR = 'Aluno menor de idade: informe o responsável financeiro.';
/**
 * As regras que dependem da data de HOJE, no fuso da arena.
 *
 * Funciona sobre o estado FINAL do cadastro — no cadastro novo, o que
 * veio no corpo; na alteração, o gravado com as mudanças por cima. É o
 * que impede apagar o responsável de quem continua menor.
 */
export function problemasDoCadastro(dados, hoje) {
    const problemas = [];
    const { nascimento } = dados;
    if (nascimento) {
        if (nascimento > hoje) {
            problemas.push({ campo: 'nascimento', mensagem: 'A data de nascimento está no futuro.' });
        }
        else if (nascimento < '1900-01-01') {
            problemas.push({ campo: 'nascimento', mensagem: 'Confira o ano de nascimento.' });
        }
        else if (ehMenorDeIdade(nascimento, hoje) && !dados.responsavelFinanceiro?.trim()) {
            problemas.push({ campo: 'responsavelFinanceiro', mensagem: MENSAGEM_RESPONSAVEL_DO_MENOR });
        }
    }
    // Um vínculo que começa amanhã é agendamento, não cadastro.
    if (dados.desde && dados.desde > hoje) {
        problemas.push({ campo: 'desde', mensagem: 'O início do vínculo não pode estar no futuro.' });
    }
    return problemas;
}
/**
 * `zNovoAluno` com as regras de hoje — o que o formulário usa.
 *
 * É uma FÁBRICA porque "hoje" não é constante: é o dia no fuso da arena,
 * que o servidor calcula e manda junto com os indicadores. O servidor
 * aplica exatamente as mesmas regras, pela mesma função.
 */
export function zNovoAlunoEm(hoje) {
    return zNovoAluno.superRefine((dados, ctx) => {
        for (const problema of problemasDoCadastro(dados, hoje)) {
            ctx.addIssue({ code: 'custom', path: [problema.campo], message: problema.mensagem });
        }
    });
}
/**
 * O corpo de `PATCH /alunos/:id` — os dados da ficha. Tudo opcional;
 * `null` apaga o campo opcional.
 *
 * NÃO tem `status`: situação muda pelos casos de uso de ciclo de vida.
 * Um `status` no corpo é descartado, e o pedido fica sem nada para
 * alterar.
 */
export const zAlteracaoDeAluno = z
    .object({
    nome: zNomeDePessoa.optional(),
    apelido: textoOpcional(40).nullable().optional(),
    telefone: zTelefone.optional(),
    nascimento: zDataReal.optional(),
    cpf: zCpf.nullable().optional(),
    email: z.string().trim().toLowerCase().email('E-mail inválido.').nullable().optional(),
    origem: z.enum(ORIGEM_ALUNO).nullable().optional(),
    contatoEmergenciaNome: textoOpcional(80).nullable().optional(),
    contatoEmergenciaTelefone: zTelefone.nullable().optional(),
    responsavelFinanceiro: textoOpcional(120).nullable().optional(),
    observacoesMedicas: textoOpcional(1000).nullable().optional(),
})
    .refine((a) => Object.keys(a).length > 0, { message: 'Nada para alterar.' });
/** Ativar e inativar: o motivo é opcional, mas fica no histórico quando vem. */
export const zMudancaDeStatus = z.object({
    motivo: textoOpcional(300).optional(),
});
/** Cancelar: o protótipo declara "motivo obrigatório registrado". */
export const zCancelamentoDeAluno = z.object({
    motivo: z
        .string({ required_error: 'Diga por que o cadastro foi cancelado.' })
        .trim()
        .min(3, 'Diga por que o cadastro foi cancelado.')
        .max(300, 'No máximo 300 caracteres.'),
});
export const zConsentimento = z.object({ tipo: z.enum(TIPOS_DE_CONSENTIMENTO) });
/**
 * A confirmação forte da anonimização: o nome completo do aluno,
 * digitado. O servidor confere — sem acento e sem diferença de caixa —
 * contra o nome gravado.
 */
export const zAnonimizacao = z.object({
    confirmacao: z.string({ required_error: 'Digite o nome do aluno para confirmar.' }).trim().min(1),
});
/**
 * A query de `GET /alunos`. Paginação por CURSOR, como toda listagem.
 *
 * Sem `status`, a listagem é a BASE VIVA — experimentais, ativos e
 * inativos. Cancelado aparece só no filtro próprio: o protótipo lista
 * "Todos" sem nenhum cancelado, e cadastros anonimizados ("Aluno #0042")
 * ocupariam o topo da ordem alfabética para sempre.
 */
export const zConsultaDeAlunos = zPaginacao.extend({
    busca: z.string().trim().max(80, 'Busca longa demais.').optional(),
    status: z.enum(STATUS_ALUNO).optional(),
    limite: z.coerce.number().int().min(1).max(100).default(25),
});
/** "Este telefone já tem cadastro?" — o aviso de `#m-novo-aluno`. */
export const zConsultaDeDuplicados = z.object({
    telefone: zTelefone,
    /** Ao editar a ficha, o próprio aluno não é duplicado de si. */
    ignorar: zId.optional(),
});
// --- 09 · importação: a porta da planilha ----------------------------
/**
 * As situações com que um cadastro pode ENTRAR pela importação.
 *
 * Uma a mais que `STATUS_INICIAIS`, e de propósito: "ninguém nasce
 * inativo" vale para quem se cadastra na recepção hoje. A planilha é
 * outra coisa — é a base que já existia, com o ex-aluno que parou em
 * março. Importá-lo como Ativo inflaria o KPI; deixá-lo de fora perderia
 * o histórico. Cancelado continua fora: cancelar exige motivo, e
 * cancelamento é caso de uso, não linha de planilha.
 */
export const STATUS_IMPORTAVEIS = ['Experimental', 'Ativo', 'Inativo'];
/**
 * Um aluno como a importação o grava — o documento 09 entra pela MESMA
 * porta do cadastro: os mesmos esquemas de campo de `#m-novo-aluno`, e
 * `problemasDoCadastro` por cima, com o hoje da arena.
 *
 * O que muda é o que a planilha não pode dizer: não há aceite de termo
 * (consentimento é ato do titular, não se infere de planilha), a origem
 * é sempre `Importacao`, e dado de saúde não entra por aqui.
 */
export const zAlunoImportado = z
    .object(camposDoCadastro)
    .omit({ origem: true, observacoesMedicas: true })
    .extend({
    status: z.enum(STATUS_IMPORTAVEIS, {
        errorMap: () => ({ message: 'Na importação, o status é Experimental, Ativo ou Inativo.' }),
    }),
    desde: zDataReal.optional(),
});
//# sourceMappingURL=alunos.js.map